import { action, computed, observable } from "mobx";
import { message } from "antd";
import { IObjectMap, LoadDeclarants, Row } from "../../interfaces/common";
import Manager, { IManager } from "../../services/Manager";
import { PaginationResponse } from "../../interfaces/response";
import ApiManager from "../../mixins/ApiManager";
import { Client } from "../../models/ByClients/Client";
import { Item } from "../../models/ByClients/Item";
import isNaN from "../../helpers/number";
import findCustomGroupById from "../../utils/findCustomGroupById";
import findSelectedEntities from "../../utils/findSelectedEntities";
import { Declarant } from "../../models/Declarant";
import { RefreshInterface } from "../interfaces/RefreshInterface";
import {addMessage, callMessages} from "../../helpers/flashMessage";

interface IByClientsOrDepartmentDataStore {
  _data: Object[];
  customGroupsEntities: Declarant[];
  selectedRows: Row[];
}

abstract class AbstractLoaderStore
  extends ApiManager
  implements LoadDeclarants, RefreshInterface {
  protected apiManager: IManager = Manager;

  @observable
  _data: Object[] = [];

  @computed
  get data(): Object[] {
    return this._data;
  }

  @observable
  hasMoreItems = false;

  @observable
  isLoadingTableData = false;

  protected _parameters: IObjectMap = {};

  get parameters(): IObjectMap {
    return this._parameters;
  }

  load(parameters: IObjectMap | null, needUpdateData?: boolean): void {
    if (this.isLoadingTableData) {
      return;
    }

    this.isLoadingTableData = true;

    if (needUpdateData) {
      this._parameters = {
        ...this._parameters,
        ...(parameters || {}),
        page: 1,
      };
    } else {
      this._parameters = {
        ...this._parameters,
        page: (this._parameters.page as number) + 1,
      };
    }


    this.getLoadResponse({
      ...this._parameters,
      ...parameters,
    }).then(async (response) => {
      return this.finishLoading(response, !!needUpdateData);
    }).catch(async (err) => {
      const error = await err.json();
      addMessage(`Error when trying to load data. Please contact our support team.`, 'error');
      console.error('Load Error Context: \n', error.message);
      callMessages(5);
      this.isLoadingTableData = false;
    });
  }

  protected async finishLoading(
    response: Response,
    needUpdateData: boolean
  ): Promise<Response> {
    const {
      data,
      current_page: currentPage,
      last_page: lastPage,
    } = (await response.json()) as PaginationResponse;

    this._parameters = {
      ...this._parameters,
      page: currentPage,
    };

    this._data = needUpdateData ? data : this._data.concat(data);
    this.hasMoreItems = currentPage < lastPage;
    this.isLoadingTableData = false;

    return response;
  }

  abstract getLoadResponse(parameters: IObjectMap): Promise<Response>;

  async groupDeclarants(
    selectedRows: unknown[],
    store: IByClientsOrDepartmentDataStore
  ): Promise<void> {
    store._data = await Promise.all(
      (store._data as Client[]).map(async (client) => {
        const keys: Row[] = client.children.reduce<Row[]>(
          (values, declarant) => {
            if (selectedRows.includes(declarant.id as Row)) {
              return [...values, declarant.id] as Row[];
            }

            return values;
          },
          []
        );

        if (keys.length > 0) {
          const group = await this.sendGroupRequest(client, keys);

          if (group) {
            const data = client.children
              .filter((item) => !keys.includes(item.id as Row))
              .filter(
                (item) =>
                  !(item.id as number).toString().startsWith("custom_group")
              );

            return {
              ...client,
              children: [...data, ...group],
            };
          }
        }

        return client;
      })
    );
    store.selectedRows = [];
    store.customGroupsEntities = [];
  }

  async sendGroupRequest(client: Client, items: Row[]): Promise<Item[] | null> {
    return this.apiManager
      .groupDeclarants(client.key, items)
      .then(async (response) => {
        const data = await response.json();

        message.success(data.message);

        this.load(
          {
            ...this._parameters,
            page: 0,
          },
          true
        );

        return data.group as Item[];
      })
      .catch(async (response) => {
        const data = await response.json();
        message.error(data.message);

        return null;
      });
  }

  async sendUngroupRequest(
    selectedRows: unknown[],
    store: IByClientsOrDepartmentDataStore
  ): Promise<void> {
    const keys = selectedRows.filter((key) => !isNaN(key));

    if (keys.length === 0) {
      return;
    }

    const items = store.customGroupsEntities.filter((entity) =>
      keys.includes(entity.id as Row)
    );

    if (items.length === 0) {
      return;
    }

    const group = new Map();

    items.forEach((item) => {
      if (group.has(item.client_id)) {
        group.set(item.client_id, [...group.get(item.client_id), item.id]);
        return;
      }

      group.set(item.client_id, [item.id]);
    });

    const promises: Promise<unknown>[] = [];

    group.forEach((value, key) => {
      promises.push(this.apiManager.ungroupDeclarants(key, value));
    });

    Promise.all(promises)
      .then(() => {
        this.load(
          {
            ...this._parameters,
            page: 0,
          },
          true
        );
        store.selectedRows = [];
        store.customGroupsEntities = [];
      })
      .catch(async (response) => {
        const data = await response.json();
        message.error(data.message);
      });
  }

  @action
  joinEntities(selectedRows: Row[]): void {
    const customGroups = selectedRows.filter((key) =>
      key.toString().startsWith("custom_group")
    );

    if (customGroups.length > 1) {
      return;
    }

    const customGroup = findCustomGroupById(
      customGroups[0] as string,
      this._data as Client[]
    );

    if (!customGroup) {
      return;
    }

    const items = findSelectedEntities(selectedRows, this._data as Client[]);

    if (items.length > 0) {
      this.apiManager
        .attachDeclarantToCustomEntities(customGroup.key, items)
        .then(async (response) => {
          const data = await response.json();
          this.load(
            {
              ...this._parameters,
              page: 0,
            },
            true
          );
          message.success(data.message);
        })
        .catch(async (response) => {
          const data = await response.json();
          message.error(data.message);

          return null;
        });
    }
  }

  refresh(): void {
    this.load(
      {
        ...this.parameters,
        page: 1,
      },
      true
    );
  }
}

export default AbstractLoaderStore;
